import React from "react";
import { Lock } from "lucide-react";
import { LAYER_GROUPS, LAYERS, OVERLAYS } from "../config/layers";
import { CLASSIFICATION } from "../data/provenance";
import { layerCounts, groupCount } from "../services/metrics";

/* Left panel: layer and overlay controls (§7).
 *
 * Grouped rather than a flat list of 20 switches — an operator scans for
 * "medical" and finds three things, instead of reading every label.
 *
 * Layers the current role may not see are rendered as locked rows rather than
 * hidden. Showing that a restricted layer *exists* but is not available is
 * more honest than pretending the capability is absent, and it tells a senior
 * officer what they would gain by escalating. */
export default function LayerPanel({
  lang, layerState, overlayState, onToggleLayer, onToggleOverlay,
  allowedLayerIds, onToggleGroup,
}) {
  const isAllowed = (id) => !allowedLayerIds || allowedLayerIds.includes(id);
  /* Counts are static for the demo dataset, so compute once. They matter
     because a toggle with 0 behind it looks broken when switched on — the
     badge says "empty" before the operator wonders why nothing appeared. */
  const counts = React.useMemo(() => layerCounts(), []);

  return (
    <div className="flex flex-col">
      <div className="px-3 py-2.5 border-b border-slate-200 sticky top-0 bg-white z-10">
        <div className="text-[11px] font-black uppercase tracking-wider text-slate-500">
          {lang === "mr" ? "नकाशा स्तर" : "Map Layers"}
        </div>
      </div>

      {/* Analytical overlays first — they are what an operator changes most. */}
      <section className="px-3 py-3 border-b border-slate-200">
        <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">
          {lang === "mr" ? "विश्लेषण स्तर" : "Intelligence Overlays"}
        </div>
        <div className="space-y-1.5">
          {OVERLAYS.map((o) => (
            <Row
              key={o.id}
              label={o.label[lang] ?? o.label.en}
              checked={!!overlayState[o.id]}
              onChange={() => onToggleOverlay(o.id)}
            />
          ))}
        </div>
      </section>

      {LAYER_GROUPS.map((group) => {
        const groupLayers = LAYERS.filter((l) => l.group === group.key);
        const anyOn = groupLayers.some((l) => layerState[l.id] && isAllowed(l.id));
        return (
          <section key={group.key} className="px-3 py-3 border-b border-slate-200 last:border-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {group.label[lang] ?? group.label.en}
                </span>
                <span className="text-[9px] font-bold text-slate-300 tabular-nums">
                  {groupCount(group.key, counts)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onToggleGroup(group.key, !anyOn)}
                className="text-[10px] font-bold text-saffron-dark hover:underline"
              >
                {anyOn ? (lang === "mr" ? "बंद" : "None") : (lang === "mr" ? "सर्व" : "All")}
              </button>
            </div>
            <div className="space-y-1.5">
              {groupLayers.map((l) => {
                const allowed = isAllowed(l.id);
                return (
                  <Row
                    key={l.id}
                    label={l.label[lang] ?? l.label.en}
                    color={l.color}
                    checked={allowed && !!layerState[l.id]}
                    disabled={!allowed}
                    restricted={l.classification === CLASSIFICATION.RESTRICTED}
                    count={counts[l.id] ?? 0}
                    onChange={() => allowed && onToggleLayer(l.id)}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Row({ label, color, checked, onChange, disabled, restricted, count }) {
  return (
    <label
      className={`flex items-center gap-2 text-[12px] leading-tight select-none ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`}
      title={disabled ? "Restricted — your role cannot view this layer" : undefined}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="h-3.5 w-3.5 rounded border-slate-300 text-saffron-dark focus:ring-saffron/40"
      />
      {color && (
        <span
          aria-hidden
          className="h-2.5 w-2.5 rounded-full flex-shrink-0 ring-1 ring-black/10"
          style={{ background: color }}
        />
      )}
      <span className="flex-1 text-slate-700">{label}</span>
      {count != null && (
        <span
          className={`text-[9px] font-bold tabular-nums flex-shrink-0 ${
            count === 0 ? "text-slate-300" : "text-slate-400"
          }`}
          title={count === 0 ? "No features mapped for this layer" : `${count} mapped`}
        >
          {count}
        </span>
      )}
      {restricted && <Lock size={11} className="text-slate-400 flex-shrink-0" />}
    </label>
  );
}
